/* eslint-disable max-lines */
export const ssoOpenApiSpec = {
  openapi: '3.0.0',
  info: {
    title: 'Vymo APIs',
    version: '1.0.0',
    description: `
## What is this?

These APIs power **login and Single Sign-On (SSO)** for the Vymo web app. They cover:

- **Account lookup** – Find out how a user should log in (email/password, Google, Salesforce, etc.)
- **Login** – Authenticate with email/password or an external token
- **OTP / 2FA** – One-time passwords and two-factor authentication
- **Password reset** – Change password for SSO accounts

You don’t need to know SSO jargon to use this doc: each endpoint below explains **what it does**, **when to call it**, and **what to send**.

---

## Typical login flow (high level)

1. **User enters email on login screen**  
   → Your app calls **POST /sso/users/account** with the email (encrypted) to get “how does this user log in?” (e.g. Google, or email/password).

2. **User enters password (or completes external login)**  
   → Your app calls **POST /sso/authenticate** with email, password, and the account info from step 1.  
   → Server may return an auth token (success), or say “send OTP” / “reset password”.

3. **If 2FA is enabled**  
   → User gets OTP; your app calls **POST /sso/verify** with the OTP to finish login.  
   → Or **POST /sso/resend-otp** to send a new OTP.

4. **Use the token**  
   → The token comes in response headers (\`x-vymo-auth-token\` or \`auth_token\`). Your app then calls **GET /session/init** with this token to start the user’s session.

---

## Glossary

| Term | Meaning |
|------|--------|
| **SSO** | Single Sign-On – login once with a provider (e.g. Google, Salesforce) instead of a separate password. |
| **Auth token** | A string the server returns after successful login. Send it in the \`X-Vymo-Auth-Token\` header for later API calls. |
| **enc_login_id** | The user’s email (or login id) **encrypted** with PGP before sending. Used in several SSO endpoints. |
| **client_id / client** | Identifies which tenant/organization the user belongs to. Comes from the account lookup response. |
| **authentication_context** | Tells you the **type** of login (e.g. VYMO_LOGIN, OAUTH2_SALESFORCE). Also from account lookup. |
| **2FA / OTP** | Two-factor authentication; OTP = one-time password sent via email/SMS. |
    `.trim(),
  },
  servers: [
    {
      url: 'https://revyre-uat.lms.getvymo.com',
      description: 'Revyre UAT',
    },
    {
      url: 'https://staging.lms.getvymo.com',
      description: 'Staging',
    },
    { url: '/', description: 'Relative to current host' },
  ],
  tags: [
    {
      name: 'Account',
      description:
        'Look up how a user logs in (email/password, Google, Salesforce, etc.) and get client and auth type.',
    },
    {
      name: 'Login',
      description:
        'Perform login: email/password SSO, or validate an external token and get a Vymo auth token.',
    },
    {
      name: 'OTP & 2FA',
      description:
        'Verify one-time passwords (OTP) and resend OTP when two-factor authentication is enabled.',
    },
    {
      name: 'Password',
      description: 'Reset password for SSO/Vymo login accounts.',
    },
  ],
  paths: {
    '/sso/users/account': {
      post: {
        tags: ['Account'],
        summary: 'Look up how this user logs in (account context)',
        description: `
**When to use:** As soon as the user enters their email on the login screen.

**What it does:** The server looks up the user and returns:
- Whether they use **SSO** (e.g. Google, Salesforce) or **email/password**
- \`client_id\` (tenant) and \`authentication_context\` (login type)
- Optionally encrypted \`pre_login_session_data\` for later steps

**What you send:** The user’s email, encrypted as \`enc_login_id\` (PGP). You can also send query params \`local_timestamp\` and \`encrypted=true\`.

**Next step:** Use the response in **POST /sso/authenticate** (for email/password) or redirect to the right SSO provider.
        `.trim(),
        operationId: 'getAuthContext',
        parameters: [
          {
            name: 'local_timestamp',
            in: 'query',
            required: false,
            schema: { type: 'integer', example: 1700000000000 },
            description: 'Current time in milliseconds (used for validation).',
          },
          {
            name: 'encrypted',
            in: 'query',
            required: false,
            schema: { type: 'boolean', example: true },
            description: 'Set to true when sending encrypted enc_login_id.',
          },
        ],
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['enc_login_id'],
                properties: {
                  enc_login_id: {
                    type: 'string',
                    description:
                      'User’s email (or login id) encrypted with PGP. The server uses this to find the account and return login type and client info.',
                  },
                },
              },
            },
          },
        },
        responses: {
          '200': {
            description:
              'Account context: client_id, authentication_context (type), sso_login flag, and optionally pre_login_session_data (encrypted). Use this in the next authenticate call.',
          },
          '4xx': { description: 'Invalid request or user not found.' },
        },
      },
      get: {
        tags: ['Account'],
        summary: 'Get account context for Salesforce login',
        description: `
**When to use:** When your app already knows the user will log in via **Salesforce OAuth2** (e.g. a “Login with Salesforce” button).

**What it does:** Returns the same kind of account/context info as the POST version, but for the Salesforce login flow.

**What you send:** Query param \`login_type=OAUTH2_SALESFORCE\`.
        `.trim(),
        operationId: 'getSalesforceAuthContext',
        parameters: [
          {
            name: 'login_type',
            in: 'query',
            required: true,
            schema: { type: 'string', enum: ['OAUTH2_SALESFORCE'] },
            description: 'Must be OAUTH2_SALESFORCE for this flow.',
          },
        ],
        responses: {
          '200': {
            description:
              'Account/context for Salesforce SSO (client_id, etc.).',
          },
        },
      },
    },
    '/sso/verifyOtp': {
      post: {
        tags: ['OTP & 2FA'],
        summary: 'Verify OTP (one-time password)',
        description: `
**When to use:** After the user receives an OTP (e.g. via email or SMS) and enters it on your OTP verification screen.

**What it does:** Checks the OTP against the server. If valid, the server may return success and your app can proceed (e.g. to session init or next step).

**What you send:** The same \`enc_login_id\` (encrypted email) used earlier, plus \`enc_otp\` (the OTP encrypted with PGP) and \`encrypted: true\`.
        `.trim(),
        operationId: 'verifyOtp',
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: ['enc_login_id', 'enc_otp', 'encrypted'],
                properties: {
                  enc_login_id: {
                    type: 'string',
                    description:
                      'User’s email encrypted with PGP (same as in account lookup).',
                  },
                  enc_otp: {
                    type: 'string',
                    description:
                      'The OTP code the user entered, encrypted with PGP.',
                  },
                  encrypted: {
                    type: 'boolean',
                    example: true,
                    description: 'Must be true when using encrypted values.',
                  },
                },
              },
            },
          },
        },
        responses: {
          '200': { description: 'OTP verified successfully.' },
          '4xx': { description: 'Invalid or expired OTP.' },
        },
      },
    },
    '/sso/authenticate': {
      post: {
        tags: ['Login'],
        summary: 'Log in with email and password (SSO flow)',
        description: `
**When to use:** After you have the account context from **POST /sso/users/account** and the user has entered their password.

**What it does:** Authenticates the user. Possible outcomes:
- **Success** – Response headers contain \`x-vymo-auth-token\` or \`auth_token\`. Your app should then call **GET /session/init** with this token.
- **2FA required** – Response body has \`two_fa_enabled: true\`. Your app should show the OTP screen and later call **POST /sso/verify**.
- **Password reset required** – Response body has \`reset_password: true\`. Your app should show the password reset flow and use **POST /sso/resetPassword**.

**What you send:** Encrypted \`enc_login_id\`, \`password\`, \`timestamp\`, plus \`type\` and \`client\` from the account lookup response, and \`encrypted: true\`, \`source: 'web'\`.
        `.trim(),
        operationId: 'ssoAuthenticate',
        parameters: [
          {
            name: 'enc',
            in: 'query',
            required: false,
            schema: { type: 'boolean' },
            description:
              'Optional. In production/staging this is set automatically for encryption.',
          },
        ],
        requestBody: {
          required: true,
          content: {
            'application/json': {
              schema: {
                type: 'object',
                required: [
                  'enc_login_id',
                  'password',
                  'timestamp',
                  'type',
                  'client',
                  'encrypted',
                ],
                properties: {
                  enc_login_id: {
                    type: 'string',
                    description: 'User’s email encrypted with PGP.',
                  },
                  password: {
                    type: 'string',
                    description: 'User’s password, encrypted with PGP.',
                  },
                  timestamp: {
                    type: 'string',
                    description:
                      'Current timestamp encrypted with PGP (for replay protection).',
                  },
                  type: {
                    type: 'string',
                    description:
                      'Login type from account lookup, e.g. VYMO_LOGIN, LDAP, SAML.',
                    example: 'VYMO_LOGIN',
                    enum: [
                      'VYMO_LOGIN',
                      'LDAP',
                      'SAML',
                      'OAUTH2',
                      'OAUTH2_SALESFORCE',
                      'OAUTH2_AD',
                      'ONETAP_AZURE_AD',
                      'GOOGLE_AUTH_SDK',
                      'SAML_ABC',
                      'ONETAP_AD_CYBER_ARK_AMC',
                      'ONETAP_AZURE_AD_AMC',
                      'ONETAP_AZURE_AD_SECONDARY',
                    ],
                  },
                  client: {
                    type: 'string',
                    description:
                      'Client/tenant id from the account lookup response.',
                  },
                  encrypted: {
                    type: 'boolean',
                    example: true,
                    description: 'Must be true when sending encrypted fields.',
                  },
                  source: {
                    type: 'string',
                    example: 'web',
                    description: 'Identifies the client (e.g. web app).',
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'Success, 2FA required, or password reset required. Check headers and body.',
              headers: {
                'x-vymo-auth-token': {
                  schema: { type: 'string' },
                  description:
                    'Use this token in GET /session/init to start the session.',
                },
                auth_token: {
                  schema: { type: 'string' },
                  description:
                    'Alternative header name for the same auth token.',
                },
              },
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      reset_password: {
                        type: 'boolean',
                        description:
                          'If true, user must reset password; use POST /sso/resetPassword.',
                      },
                      two_fa_enabled: {
                        type: 'boolean',
                        description:
                          'If true, show OTP screen and then call POST /sso/verify.',
                      },
                      error: {
                        type: 'string',
                        description: 'Error message if login failed.',
                      },
                    },
                  },
                },
              },
            },
            '4xx': { description: 'Invalid credentials or request.' },
          },
        },
      },
      '/sso/authByToken': {
        post: {
          tags: ['Login'],
          summary: 'Log in using an external token',
          description: `
**When to use:** When the user has already logged in with an **external system** (e.g. your company’s identity provider) and you have a \`client_id\` and \`access_token\` for that user.

**What it does:** The server validates the external token and, if valid, returns a **Vymo auth token** in the response headers. Your app then uses that token with **GET /session/init** to start the session.

**What you send:** \`type: 'EXTERNAL_AUTH'\`, plus \`client\` (tenant id) and \`access_token\` from the external provider.
        `.trim(),
          operationId: 'checkIfAuthIsValid',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: ['type', 'client', 'access_token'],
                  properties: {
                    type: {
                      type: 'string',
                      example: 'EXTERNAL_AUTH',
                      description: 'Must be EXTERNAL_AUTH for this flow.',
                    },
                    client: {
                      type: 'string',
                      description: 'Client/tenant identifier.',
                    },
                    access_token: {
                      type: 'string',
                      description:
                        'Auth token from the external identity provider.',
                    },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'Token valid. Use the returned Vymo auth token with GET /session/init.',
              headers: {
                'x-vymo-auth-token': { schema: { type: 'string' } },
                auth_token: { schema: { type: 'string' } },
              },
            },
            '4xx': {
              description:
                'Invalid or expired external token; may include authentication_error.',
            },
          },
        },
      },
      '/sso/authenticate/getAuthToken': {
        post: {
          tags: ['Login'],
          summary: 'Get Vymo auth token from session ids (rid & sid)',
          description: `
**When to use:** When you have **rid** and **sid** (session identifiers, e.g. from cookies or a redirect) and need to convert them into a Vymo auth token.

**What it does:** Exchanges \`rid\` and \`sid\` for a single auth token (\`context\`) that you can use with **GET /session/init**.

**What you send:** JSON body with \`rid\` and \`sid\` (both required).
        `.trim(),
          operationId: 'getAuthTokenUsingRidSid',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: ['rid', 'sid'],
                  properties: {
                    rid: {
                      type: 'string',
                      description:
                        'Session identifier (e.g. from cookie or URL).',
                    },
                    sid: {
                      type: 'string',
                      description:
                        'Session identifier (e.g. from cookie or URL).',
                    },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'Auth token is in the response body under "context".',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    required: ['context'],
                    properties: {
                      context: {
                        type: 'string',
                        description:
                          'Vymo auth token. Pass this to GET /session/init.',
                      },
                    },
                  },
                },
              },
            },
            '4xx': {
              description: 'Invalid rid/sid or authentication_error.',
            },
          },
        },
      },
      '/sso/resetPassword': {
        post: {
          tags: ['Password'],
          summary: 'Reset password (force change)',
          description: `
**When to use:** When the server has asked the user to reset their password (e.g. after **POST /sso/authenticate** returned \`reset_password: true\`) and the user has entered their old password and new password.

**What it does:** Updates the user’s password. After success, the user can log in again with the new password via **POST /sso/authenticate**.

**What you send:** Encrypted \`enc_login_id\`, \`new_password\`, \`password\` (old password), \`timestamp\`, \`client\` from account context, \`type: 'VYMO_LOGIN'\`, \`encrypted: true\`, \`source: 'web'\`.
        `.trim(),
          operationId: 'forceResetPassword',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: [
                    'enc_login_id',
                    'new_password',
                    'password',
                    'timestamp',
                    'client',
                    'type',
                    'encrypted',
                  ],
                  properties: {
                    enc_login_id: {
                      type: 'string',
                      description: 'User’s email encrypted with PGP.',
                    },
                    new_password: {
                      type: 'string',
                      description: 'New password (encrypted with PGP).',
                    },
                    password: {
                      type: 'string',
                      description:
                        'Current (old) password, encrypted with PGP.',
                    },
                    timestamp: {
                      type: 'string',
                      description: 'Timestamp encrypted with PGP.',
                    },
                    client: {
                      type: 'string',
                      description: 'Client/tenant id from account context.',
                    },
                    type: {
                      type: 'string',
                      example: 'VYMO_LOGIN',
                      description:
                        'Login type; use VYMO_LOGIN for password reset.',
                    },
                    encrypted: {
                      type: 'boolean',
                      example: true,
                    },
                    source: {
                      type: 'string',
                      example: 'web',
                    },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'Password reset successful. User can now log in with the new password.',
            },
            '4xx': { description: 'Invalid old password or request.' },
          },
        },
      },
      '/sso/verify': {
        post: {
          tags: ['OTP & 2FA'],
          summary: 'Verify 2FA OTP and complete login',
          description: `
**When to use:** After **POST /sso/authenticate** returned \`two_fa_enabled: true\` and the user has received and entered the OTP.

**What it does:** Verifies the 2FA OTP. If valid, the response **headers** contain \`x-vymo-auth-token\` or \`auth_token\`. Your app should then call **GET /session/init** with this token to complete the login and start the session.

**What you send:** Encrypted \`timestamp\` and \`otp\`, plus \`encrypted: true\`. You may also need to send \`two_fa_id\` or other fields from the authenticate response (see app implementation).
        `.trim(),
          operationId: 'verify2FAOtp',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: ['timestamp', 'otp', 'encrypted'],
                  properties: {
                    timestamp: {
                      type: 'string',
                      description:
                        'Server timestamp encrypted with PGP (from GET /get-server-time).',
                    },
                    otp: {
                      type: 'string',
                      description:
                        'The OTP code the user entered, encrypted with PGP.',
                    },
                    encrypted: {
                      type: 'boolean',
                      example: true,
                    },
                    two_fa_id: {
                      type: 'string',
                      description:
                        'Optional. 2FA context id if returned by the authenticate call.',
                    },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'OTP valid. Auth token is in response headers; call GET /session/init with it.',
              headers: {
                'x-vymo-auth-token': { schema: { type: 'string' } },
                auth_token: { schema: { type: 'string' } },
              },
            },
            '4xx': {
              description:
                'Invalid OTP or error in body (e.g. response.data.error).',
            },
          },
        },
      },
      '/sso/resend-otp': {
        post: {
          tags: ['OTP & 2FA'],
          summary: 'Resend 2FA OTP',
          description: `
**When to use:** When the user is on the 2FA/OTP screen and requests “Resend OTP” (e.g. they didn’t receive it or it expired).

**What it does:** Sends a new OTP to the user. Your app can then collect the new OTP and call **POST /sso/verify** as usual.

**What you send:** Encrypted \`timestamp\` (from GET /get-server-time), plus any other payload fields your app uses (e.g. \`two_fa_id\`).
        `.trim(),
          operationId: 'resend2FAOtp',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: ['timestamp'],
                  properties: {
                    timestamp: {
                      type: 'string',
                      description: 'Server timestamp encrypted with PGP.',
                    },
                    two_fa_id: {
                      type: 'string',
                      description: 'Optional. 2FA context id if applicable.',
                    },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description:
                'New OTP sent. User can enter it and call POST /sso/verify.',
            },
            '4xx': {
              description: 'Request failed (e.g. too many resend attempts).',
            },
          },
        },
      },
    },
  },
} as const;
